import { usernameSchema, uuidSchema } from '../../contracts/src/validation';

export const SITE_ORIGIN = 'https://garagegymbros.com';
export const SITE_NAME = 'Garage Gym Bros';
export const SITE_DESCRIPTION =
  'Built at home. Never alone. Connect with home and garage gym owners, share your setup, and keep your gym and community in one place.';
export const SOCIAL_IMAGE = `${SITE_ORIGIN}/branding/social-card.png`;
// Existing owner-operated review/role-test accounts remain usable but are not search content.
export const SEARCH_EXCLUDED_USERNAMES = [
  'apple_review',
  'ggbadmin',
  'ggbbusiness',
  'ggbeditor',
  'ggbmoderator',
  'ggbuser',
] as const;
export const PUBLIC_SITE_PAGES = [
  {
    path: '/',
    title: 'Garage Gym Bros — Home Gym Community',
    heading: SITE_NAME,
    description: SITE_DESCRIPTION,
    excerpt: 'Built at home. Never alone.',
  },
  {
    path: '/policies/community',
    title: 'Community Guidelines | Garage Gym Bros',
    heading: 'Keep the garage welcoming.',
    description:
      'Read the Garage Gym Bros community guidelines for respectful posts, honest reviews, privacy, reports and moderation.',
    excerpt:
      'Be constructive, respect different budgets and spaces, and share first-hand experience honestly. Criticism and low ratings are welcome when relevant and respectful.',
  },
  {
    path: '/policies/privacy',
    title: 'Privacy Policy | Garage Gym Bros',
    heading: 'Your privacy matters.',
    description:
      'Learn how Garage Gym Bros handles account data, photo privacy, visibility choices, account exports and deletion requests.',
    excerpt:
      'Members choose their profile, equipment, and content visibility. Purchase amounts, receipts, and private notes are private. Shared catalog facts remain independent of member purchase history.',
  },
  {
    path: '/policies/account-deletion',
    title: 'Delete your account | Garage Gym Bros',
    heading: 'Delete your GarageGymBros account',
    description: 'Request account and data deletion from GarageGymBros on the web or by email.',
    excerpt:
      'Open Settings → Privacy & data → Delete account, or email admin@garagegymbros.com to request deletion if you can’t sign in.',
  },
  {
    path: '/policies/child-safety',
    title: 'Child Safety Standards | Garage Gym Bros',
    heading: 'Child safety standards',
    description: 'Read the GarageGymBros child safety standards and how to report abuse.',
    excerpt:
      'GarageGymBros is an adults-only community. Child sexual abuse and exploitation are prohibited. Report violations in the app or contact admin@garagegymbros.com.',
  },
  {
    path: '/policies/terms',
    title: 'Community Terms | Garage Gym Bros',
    heading: 'Community terms',
    description:
      'Read the Garage Gym Bros terms for members, community content, equipment information and local equipment trades.',
    excerpt:
      'GarageGymBros is a community for garage and home gym enthusiasts aged 18 or older. Share content you have permission to publish and respect other members’ privacy and visibility choices. Features and availability are subject to service limits.',
  },
] as const;

export type PublicSeoProfile = {
  username: string;
  display_name: string;
  bio: string;
  visibility: string;
  status: string;
  is_demo?: boolean | null;
};
export type PageSeo = {
  title: string;
  description: string;
  canonical: string | null;
  indexable: boolean;
  kind: 'home' | 'policy' | 'profile' | 'private' | 'missing';
  heading: string;
  excerpt: string;
  structuredData: Record<string, unknown>[];
};
const organization = {
  '@type': 'Organization',
  '@id': `${SITE_ORIGIN}/#organization`,
  name: SITE_NAME,
  url: SITE_ORIGIN,
  logo: `${SITE_ORIGIN}/branding/ggb-logo.svg`,
};

export function publicProfileIdentifier(
  path: string,
): { username: string } | { id: string } | null {
  const id = /^\/profile\/([^/]+)\/?$/.exec(path)?.[1];
  if (id && uuidSchema.safeParse(id).success) return { id };
  if (!/^\/[^/]+\/?$/.test(path)) return null;
  try {
    const parsed = usernameSchema.safeParse(decodeURIComponent(path.replace(/^\/|\/$/g, '')));
    return parsed.success ? { username: parsed.data } : null;
  } catch {
    return null;
  }
}

export function pageSeo(path: string, profile?: PublicSeoProfile | null): PageSeo {
  const pathname = path === '/index.html' ? '/' : path.replace(/\/$/, '') || '/';
  const page = PUBLIC_SITE_PAGES.find((item) => item.path === pathname);
  if (page) {
    const canonical = SITE_ORIGIN + page.path;
    return {
      ...page,
      canonical,
      indexable: true,
      kind: page.path === '/' ? 'home' : 'policy',
      structuredData: [
        {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          '@id': `${SITE_ORIGIN}/#website`,
          name: SITE_NAME,
          url: SITE_ORIGIN,
          description: SITE_DESCRIPTION,
          publisher: { '@id': organization['@id'] },
        },
        { '@context': 'https://schema.org', ...organization },
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': canonical,
          url: canonical,
          name: page.title,
          description: page.description,
          isPartOf: { '@id': `${SITE_ORIGIN}/#website` },
        },
      ],
    };
  }
  if (publicProfileIdentifier(pathname)) {
    const username = profile && usernameSchema.safeParse(profile.username);
    if (
      profile &&
      username?.success &&
      profile.status === 'active' &&
      profile.visibility === 'public'
    ) {
      const canonical = `${SITE_ORIGIN}/${username.data}`;
      const name = profile.display_name.trim().slice(0, 100) || username.data;
      const bio = profile.bio.replace(/\s+/g, ' ').trim();
      const description =
        bio.slice(0, 160) ||
        `View @${username.data}’s public profile and connect with home gym owners on Garage Gym Bros.`;
      const indexable =
        profile.is_demo !== true &&
        !SEARCH_EXCLUDED_USERNAMES.includes(
          username.data as (typeof SEARCH_EXCLUDED_USERNAMES)[number],
        );
      return {
        title: `${name} (@${username.data}) | Garage Gym Bros`,
        description,
        canonical,
        indexable,
        kind: 'profile',
        heading: name,
        excerpt: bio.slice(0, 2000),
        structuredData: indexable
          ? [
              {
                '@context': 'https://schema.org',
                '@type': 'ProfilePage',
                '@id': canonical,
                url: canonical,
                name: `${name} (@${username.data})`,
                mainEntity: {
                  '@type': 'Person',
                  name,
                  alternateName: `@${username.data}`,
                  url: canonical,
                },
              },
            ]
          : [],
      };
    }
    return {
      title: 'Member Profile | Garage Gym Bros',
      description: 'Sign in to Garage Gym Bros to connect with your community.',
      canonical: null,
      indexable: false,
      kind: profile === null ? 'missing' : 'private',
      heading: 'Member profile',
      excerpt: '',
      structuredData: [],
    };
  }
  const names: Record<string, string> = {
    home: 'Home',
    discover: 'Discover',
    catalog: 'Equipment',
    companies: 'Companies',
    'my-gym': 'My Gym',
    'my-profile': 'My Profile',
    messages: 'Messages',
    settings: 'Settings',
    notifications: 'Notifications',
    admin: 'Administration',
    flexes: 'Flexes',
    stories: 'Flexes',
    forum: 'Forum',
    trade: 'Trade',
    account: 'Account',
    profile: 'Profile',
    auth: 'Sign in',
  };
  const name = names[pathname.split('/')[1]];
  return {
    title: `${name ?? 'Page unavailable'} | Garage Gym Bros`,
    description: 'Sign in to your Garage Gym Bros account. Built at home. Never alone.',
    canonical: name && /^\/[a-zA-Z0-9/_-]+$/.test(pathname) ? SITE_ORIGIN + pathname : null,
    indexable: false,
    kind: name ? 'private' : 'missing',
    heading: name ?? 'Page unavailable',
    excerpt: '',
    structuredData: [],
  };
}

export function robotsDirective(seo: PageSeo): string {
  return seo.indexable ? 'index, follow, max-image-preview:large' : 'noindex, follow';
}
export function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]!,
  );
}
export function serializeStructuredData(value: unknown): string {
  return JSON.stringify(value).replace(
    /[<>&\u2028\u2029]/g,
    (character) => `\\u${character.charCodeAt(0).toString(16).padStart(4, '0')}`,
  );
}
export function renderSeoHead(seo: PageSeo): string {
  const tag = (name: string, content: string, property = false) =>
    `<meta data-ggb-seo ${property ? 'property' : 'name'}="${name}" content="${escapeHtml(content)}">`;
  return [
    `<title>${escapeHtml(seo.title)}</title>`,
    tag('description', seo.description),
    tag('robots', robotsDirective(seo)),
    seo.canonical ? `<link data-ggb-seo rel="canonical" href="${escapeHtml(seo.canonical)}">` : '',
    tag('og:site_name', SITE_NAME, true),
    tag('og:type', 'website', true),
    tag('og:locale', 'en_US', true),
    tag('og:title', seo.title, true),
    tag('og:description', seo.description, true),
    seo.canonical ? tag('og:url', seo.canonical, true) : '',
    tag('og:image', SOCIAL_IMAGE, true),
    tag('og:image:width', '1200', true),
    tag('og:image:height', '630', true),
    tag('og:image:alt', 'Garage Gym Bros. Built at home. Never alone.', true),
    tag('twitter:card', 'summary_large_image'),
    tag('twitter:title', seo.title),
    tag('twitter:description', seo.description),
    tag('twitter:image', SOCIAL_IMAGE),
    tag('twitter:image:alt', 'Garage Gym Bros. Built at home. Never alone.'),
    ...seo.structuredData.map(
      (item) =>
        `<script data-ggb-seo type="application/ld+json">${serializeStructuredData(item)}</script>`,
    ),
  ]
    .filter(Boolean)
    .join('\n');
}
export function renderPublicContent(seo: PageSeo): string {
  if (!seo.indexable) return '';
  const home = seo.kind === 'home';
  return `<main class="seo-public" id="main-content"><a href="/"><img src="/branding/ggb-logo.svg" width="280" height="205" alt="Garage Gym Bros"></a><h1>${escapeHtml(home ? 'Built at home. Never alone.' : seo.heading)}</h1>${!home && seo.excerpt ? `<p>${escapeHtml(seo.excerpt)}</p>` : ''}<p><a href="/auth/signin">Sign in or join</a></p><nav aria-label="Community policies"><a href="/policies/community">Community guidelines</a> · <a href="/policies/privacy">Privacy</a> · <a href="/policies/terms">Terms</a></nav></main>`;
}
export function renderSeoDocument(html: string, seo: PageSeo): string {
  return html
    .replace(
      /<!--ggb-seo-head-->[\s\S]*?<!--\/ggb-seo-head-->/,
      () => `<!--ggb-seo-head-->\n${renderSeoHead(seo)}\n<!--/ggb-seo-head-->`,
    )
    .replace(
      /<!--ggb-public-content-->[\s\S]*?<!--\/ggb-public-content-->/,
      () => `<!--ggb-public-content-->${renderPublicContent(seo)}<!--/ggb-public-content-->`,
    );
}
